import { z } from 'zod'
import { adresseDe, expediteurCorrespond, motifExpediteur } from './consignes'
import { normaliserLibelle } from './libelle'
import { libelleDuTiersReconnu } from './rapprochement'
import { empreinteNomFournisseur } from './recherche-pieces'

// Fiche du fournisseur complétée sur proposition (décision de Pierre-Marie du 14/10/2026) : quand
// une pièce venue d'un mail est rapprochée d'une opération du cabinet, la fiche de son
// fournisseur reçoit, si elle ne les a pas, le libellé bancaire de l'opération et l'adresse qui a
// envoyé la pièce ; la recherche des pièces cherchera ensuite ses factures par cet expéditeur et
// le montant. Une proposition, jamais écrite seule : un humain la valide ou la refuse.

/** La décision « Compléter la fiche du fournisseur », sur la pièce rapprochée. */
export const ACTION_FICHE_FOURNISSEUR = 'completer_fiche_fournisseur'
/** Adresses d'une fiche, au plus (comme la saisie du référentiel). */
const ADRESSES_MAX = 10

/** Ce que la proposition ajoute à la fiche : un libellé bancaire, une adresse d'envoi, ou les deux. */
export type FicheProposee = { libelle: string | null; adresse: string | null }

type LibellesEtAdresses = { libelles: readonly string[]; adresses: readonly string[] }

/**
 * Le libellé bancaire proposé : le marchand de l'opération, normalisé, jusqu'au premier mot qui
 * porte un chiffre (« FICTIF.COM 1224819309 » : FICTIF.COM), trois signes au moins, et reconnu
 * dans le libellé de l'opération par la règle du rapprochement. Null : pas de marchand, ou rien
 * de sûr.
 */
export function libelleBancairePropose(op: { libelle: string; marchand: string | null }) {
  if (!op.marchand) return null
  const mots: string[] = []
  for (const m of normaliserLibelle(op.marchand).split(' ')) {
    if (/\d/.test(m)) break
    mots.push(m)
  }
  const libelle = mots.join(' ')
  if (libelle.replace(/[^A-Z0-9]/g, '').length < 3) return null
  return libelleDuTiersReconnu([libelle], normaliserLibelle(op.libelle)) ? libelle : null
}

/**
 * Ce qu'il faut proposer d'ajouter à la fiche du fournisseur d'une pièce rapprochée de son
 * opération : le libellé bancaire si la fiche ne reconnaît pas encore celui de l'opération,
 * l'adresse d'envoi de la pièce si aucune de ses adresses ne la couvre. Jamais ce qu'un autre
 * tiers porte déjà (deux fiches reconnues, la recherche n'en retient aucune), ce qu'un humain a
 * refusé, ni une adresse de plus sur une fiche pleine. Null : rien à proposer.
 */
export function propositionFicheFournisseur(d: {
  tiers: LibellesEtAdresses
  /** Les autres tiers : ce qu'ils portent n'est jamais proposé. */
  autres: readonly LibellesEtAdresses[]
  operation: { libelle: string; marchand: string | null }
  /** En-tête de l'expéditeur de la pièce (« Nom <adresse> »). */
  expediteur: string | null
  /** Propositions refusées, telles que le journal les garde (`empreintesFiche`). */
  refusees: readonly FicheProposee[]
}): FicheProposee | null {
  const refusee = (champ: keyof FicheProposee, valeur: string) =>
    d.refusees.some((r) => r[champ] === empreinteNomFournisseur(valeur))
  const libelleOp = normaliserLibelle(d.operation.libelle)
  const propose = libelleDuTiersReconnu(d.tiers.libelles, libelleOp)
    ? null
    : libelleBancairePropose(d.operation)
  const libelle =
    propose &&
    !d.autres.some((t) => libelleDuTiersReconnu(t.libelles, libelleOp)) &&
    !refusee('libelle', propose)
      ? propose
      : null
  const lue = motifExpediteur.safeParse(adresseDe(d.expediteur) ?? '')
  const a = lue.success && lue.data.includes('@') ? lue.data : null
  const couverte = (t: LibellesEtAdresses) => t.adresses.some((m) => expediteurCorrespond(m, a))
  const adresse =
    a &&
    !couverte(d.tiers) &&
    d.tiers.adresses.length < ADRESSES_MAX &&
    !d.autres.some(couverte) &&
    !refusee('adresse', a)
      ? a
      : null
  return libelle || adresse ? { libelle, adresse } : null
}

/**
 * Ce que le journal garde d'une proposition (proposée, complétée, refusée) : des empreintes,
 * jamais le libellé bancaire ni l'adresse ; elles suffisent à ne plus reproposer un refus.
 */
export function empreintesFiche(p: FicheProposee): FicheProposee {
  return {
    libelle: p.libelle && empreinteNomFournisseur(p.libelle),
    adresse: p.adresse && empreinteNomFournisseur(p.adresse),
  }
}

export function titreFicheFournisseur(nom: string): string {
  return `Compléter la fiche de ${nom}`
}

/** Ce qui est proposé, et pourquoi : retrouver seules ses prochaines pièces. */
export function descriptionFicheFournisseur(p: FicheProposee): string {
  const quoi = [
    p.libelle && `le libellé bancaire « ${p.libelle} »`,
    p.adresse && `l’adresse d’envoi ${p.adresse}`,
  ]
    .filter(Boolean)
    .join(' et ')
  return `Une pièce de ce fournisseur a été rapprochée de son opération. Ajouter à sa fiche ${quoi} : la recherche dans les mails trouvera ensuite ses factures par l’expéditeur et le montant.`
}

/** « Compléter la fiche » ou « Ne pas compléter » : rien d'autre ne se saisit. */
export const decisionFicheFournisseur = z.object({ accepter: z.boolean() }).strict()
export type DecisionFicheFournisseur = z.output<typeof decisionFicheFournisseur>
